import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, Row, Col, Tooltip, OverlayTrigger } from "react-bootstrap";
import Button from "src/components/Button/Button";
import styled from "styled-components";
import ResponsiveImage from "../ImageWrapper/ResponsiveImage";
import { formatDate, formatTime } from "src/utils/formatters";
import {
  convertTimeTo24HourFormat,
  formatTimeString
} from "src/utils/timeUtils";
import { determinePromoStatus, addFourHoursPatch } from "src/utils/promoUtils";
import { getEligibleDays } from "src/utils/promoUtils";
import { LiaTimesSolid } from "react-icons/lia";
import PromoCountdownTimer from "src/components/Promotions/PromoCountdownTimer";
import Markdown from "../Markdown";
import { MdInfo } from "react-icons/md";


const DrawCard = ({ draw, promoData }) => {
  const { t, i18n } = useTranslation();
  const currentLocale = i18n.language;
  // Kee this before the patch
  // asOf is used to show the date when the cache was last updated
  const asOf = formatTime(draw?.as_of, currentLocale);
  draw = addFourHoursPatch(draw);

  const drawEnd =  promoData?.draw_end;
  const startAt = draw?.start_date;
  const endAt = draw?.end_date;
  const promoStatus = determinePromoStatus(startAt, endAt, drawEnd);

  const ticketsEarned = draw?.total_tickets || 0;

  return (
    <StyledCard className={`${promoStatus}`}>
      <Card.Body className={`p-0 position-relative ${promoStatus}`}>
        <ResponsiveImage
          className="card-img-top"
          src={promoData.image}
          alt={draw?.promotion_name}
        />
      </Card.Body>
      <Card.Body className="p-0">
        <Card.Text className={`p-0 status-banner ${promoStatus}`}>
          {t(`promoDrawsPage.status.${promoStatus}`, { defaultValue: promoStatus })}
        </Card.Text>
        <PromoCountdownTimer startAt={startAt} endAt={endAt} drawEnd={drawEnd} />
      </Card.Body>
      <Card.Body className="perf-bottom">
        <TicketsRow className="m-0">
          <TicketCountCol className={promoStatus}>
            <span>{ticketsEarned}</span>
            <div className="position-relative">
              <Markdown>
                {t("promoDrawsPage.ticketsEarned")}
              </Markdown>
              <OverlayTrigger placement="top" overlay={
                <Tooltip>
                  {t("promoDrawsPage.cacheDisclaimer")}
                  <hr/>
                  <small>
                    {t("promoDrawsPage.asOf", { date: asOf })}
                  </small>
                </Tooltip>
              }>
                <InfoIconTrigger className="overlay-trigger">
                  <MdInfo />
                </InfoIconTrigger>
              </OverlayTrigger>
            </div>
          </TicketCountCol>
        </TicketsRow>
        <PerfBottomEdges/>
      </Card.Body>
      <Card.Body className="pt-4">
        <Card.Title>{promoData.title || draw?.promotion_name}</Card.Title>
        <Card.Text className="small mutedwhite drawDates d-none">
            <span className="text-nowrap">
              {formatDate(startAt, currentLocale)} {formatTimeString(startAt, currentLocale)}
            </span>
            <span className="text-nowrap">
              {" - "}
              {formatDate(endAt, currentLocale)} {formatTimeString(endAt, currentLocale)}
            </span>
        </Card.Text>
          <Markdown className="small mutedwhite draw-description">{promoData.description || ""}</Markdown>
        <Card.Text className="small">
            <Link to={t(`routes.${promoData?.key}`)} className="bluelinkdark">
              <strong>
                {t("promoDrawsPage.rules")}
              </strong>
            </Link>
        </Card.Text>
      </Card.Body>
      <Card.Body className="play-days perf-bottom">
        <p className="small mutedwhite">
          {t("promoDrawsPage.playDays")}
        </p>
        <Row className="eligible-days m-0 mx-auto">
          {getEligibleDays(draw).map(({ key, active }) => (
            <Col className="p-0" key={key}>
              <span className={`day-circle ${active ? "active" : "inactive"}`}>
                <span>
                  {t(`promoDrawsPage.daysOfWeek.letters.${key}`)}
                </span>
                <LiaTimesSolid />
              </span>
            </Col>
          ))}
        </Row>
          <p className="mt-2">
            {draw?.start_hour && draw?.end_hour
              ? `${convertTimeTo24HourFormat(draw?.start_hour, currentLocale)} - ${convertTimeTo24HourFormat(draw?.end_hour, currentLocale)}`
              : t("promoDrawsPage.allDay")}
          </p>
        <Row className="m-0 mt-2 play-times">
          <Col className="time-holder">
          </Col>
        </Row>
        <PerfBottomEdges/>
      </Card.Body>
      <Card.Body>
        <Button
          className="m-0"
          label={t("promoDrawsPage.cashGames")}
          href={t("routes.cashGames")}
          specialHover
          bgColor="#442c05"
          textColor="#fff"
          size="small"
        />
      </Card.Body>
    </StyledCard>
  );
};

const StyledCard = styled(Card)`
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: none;
  text-align: center;
  margin: 0;
  p.status-banner {
    margin: 0;
    text-align: center;
    width: 100%;
    font-size: 12px;
    text-transform: uppercase;
    font-weight: bold;
    color: #ffffff;
    &.starting {
      background: #583c059e;
    }
    &.during, &.drawing {
      background: #0658059e;
    }
    &.closed {
      background: #0000009e;
    }
  }

  .card-body {
    border-bottom: solid 1px #0f1112;
    &.perf-bottom {
      position: relative;
      border-bottom: solid 2px #000;
      &::after {
        content: "";
        display: block;
        position: absolute;
        bottom: -11px;
        left: 0;
        width: 100%;
        height: 20px;
        background-image: radial-gradient(circle at 10px 10px, #000 5px, transparent 5px);
        background-size: 20px 20px;
        background-repeat: repeat-x;
        background-position: center bottom;
      }
    }
  }
  .eligible-days {
    max-width: 20rem;
  }
  .play-days {
  }
  .play-times {
    text-align: center;
    .col {
      margin: 0;
      padding: 0;
      display: flex;
      justify-content: center;
      align-items: center;
    }
    .icon-holder {
      flex: 0 0 12.5%; /* 1/8 */
      max-width: 12.5%;
      display: flex;
      align-items: center;
    }

    .time-holder {
      flex: 0 0 87.5%; /* 7/8 */
      max-width: 87.5%;
      padding-right: 12.5%;
      p {
        margin: 0;
      }
    }
    svg {
      font-size: 1.6rem;
      color: #f5a623;
    }
  }
  .draw-description p strong {
    color: #f5a623;
  }

  .day-circle {
    display: flex;
    align-items: center;
    justify-content: center;
    width: clamp(1.6rem, 2.4vw, 2.5rem);
    height: clamp(1.6rem, 2.4vw, 2.5rem);
    line-height: clamp(1.6rem, 2.4vw, 3.2rem);
    font-size: clamp(0.8rem, 2vw, 1rem);
    border-radius: 50%;
    margin: 0 auto;
    font-size: 0.85rem;
    font-weight: bold;
    span {
      position: relative;
      z-index: 2;
    }
  }

  .day-circle.active {
    background-color: green;
    color: white;
    svg {
      display: none;
    }
  }

  .day-circle.inactive {
    background-color: #0f1112;
    color: var(--mutedwhite);
    position: relative;
    z-index: 2;
    svg {
      position: absolute;
      bottom: -5px;
      right: -6px;
      width: clamp(1.6rem, 1.2vw, 3.2rem);
      height: clamp(1.6rem, 1.2vw, 3.2rem);
      line-height: clamp(1.6rem, 2.4vw, 3.2rem);
      font-size: clamp(0.8rem, 2vw, 1rem);
      z-index: 2;
      color: #720909;
      opacity: 0.8;
    }
  }
`;

const PerfBottomEdges = styled.div`
  position: absolute;
  bottom: -10px;
  left: 0;
  right: 0;
  height: 20px;

  &:before, &:after {
    content: "";
    display: block;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: #000;
    position: absolute;
    bottom: 0;
    left: -10px;
  }
  &:after {
    left: auto;
    right: -10px;
  }
`;

const TicketsRow = styled(Row)`
  margin: 0;
`;

const TicketCountCol = styled(Col)`
text-align: center;
  font-size: 1rem;
  line-height: 1.2rem;
  span {
    font-weight: bold;
    font-size: 4rem;
    line-height: 4rem;
    color: #f5a623;
  }
  &.starting, &.starting span {
    color: grey;
  }
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  padding: 0 1rem;
`;

const InfoIconTrigger = styled.p`
  padding-bottom: 2px;
  font-size: 0.8rem;
  line-height: 1rem;
  position: absolute;
  top: -2px;
  right: -0.9rem;
  border-bottom: none;
  opacity: 0.4;
  &:hover {
    opacity: 0.9;
  }
`;

export default DrawCard;
